/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.03.14 at 16:07
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { forEach } from '@labor-digital/helferlein';
import { AbstractBit } from './AbstractBit';
import { DefinitionRegistry } from './Definition/DefinitionRegistry';
/**
 * The list of lifecycle hooks that must be merged when a new mixin instance is created
 */
var lifecycleHooks = ['created', 'mounted', 'unmounted', 'remounted', 'beforeDestroy', 'destroyed', 'domChanged'];
/**
 * Lets you to merge multiple bit classes into one class which allows code abstraction into mixins/traits.
 * Use it like export class MyBit extends mixins(MixinA, MixinB). All Mixin classes must extend the AbstractBit class!
 * @param ctors
 */
export function mixins() {
    var ctors = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        ctors[_i] = arguments[_i];
    }
    var mixedConstructors = [];
    var mixedLifecycleHooks = new Map();
    var mixedCtor = /** @class */ (function (_super) {
        __extends(class_1, _super);
        function class_1(context) {
            var _this = _super.call(this, context) || this;
            forEach(mixedConstructors, function (constructor) {
                constructor.call(_this, context);
            });
            forEach(mixedLifecycleHooks, function (hooks, name) {
                var _a;
                var implementedHook = (_a = _this[name]) !== null && _a !== void 0 ? _a : null;
                _this[name] = function () {
                    var _this = this;
                    forEach(hooks, function (hook) {
                        hook.call(_this);
                    });
                    if (implementedHook) {
                        implementedHook.call(this);
                    }
                };
            });
            return _this;
        }
        return class_1;
    }(AbstractBit));
    var def = DefinitionRegistry.getDefinitionFor(mixedCtor.prototype);
    forEach(ctors, function (ctor) {
        // Inherit the information from the parent class
        Object.getOwnPropertyNames(ctor.prototype).forEach(function (name) {
            var _a;
            var func = ctor.prototype[name];
            if (name !== 'constructor') {
                if (lifecycleHooks.indexOf(name) !== -1) {
                    var hooks = (_a = mixedLifecycleHooks.get(name)) !== null && _a !== void 0 ? _a : [];
                    hooks.push(func);
                    mixedLifecycleHooks.set(name, hooks);
                }
                mixedCtor.prototype[name] = func;
            }
            else {
                mixedConstructors.push(func);
            }
        });
        // Inherit the bit definition from the parent class
        def = def.mergeWith(DefinitionRegistry.getDefinitionFor(ctor.prototype));
    });
    mixedCtor.prototype.__MIXIN_DEFINITION = def;
    // @ts-ignore
    return mixedCtor;
}
//# sourceMappingURL=mixins.js.map